import { useState } from 'react'
import { api } from '../../lib/api'
import { runJs, type RunResult } from '../../lib/sandbox/runJs'

/**
 * Floor B1's three specimens.
 *
 * Split out of Specimen.tsx rather than appended to it: that file was already
 * five widgets long, and these three carry a sandbox dependency the other five
 * have no business importing.
 */

const PROBE_NAMES = [
  'window',
  'document',
  'localStorage',
  'self',
  'process',
  'require',
  'fetch',
  'caches',
  'crypto',
  'setTimeout',
  'WebSocket',
  'Worker',
]

/**
 * Three runtimes, measured rather than asserted.
 *
 * The browser column is probed in this component. The sandbox column is probed
 * by running a snippet inside the sandbox Worker. The server column is probed
 * inside the Cloudflare isolate by /api/meta/runtime. No cell is a table
 * someone typed from memory — every one is a live `typeof`, which is the only
 * form of this lesson that cannot quietly go out of date.
 */
export function RuntimeProbe() {
  const [server, setServer] = useState<Record<string, boolean> | null>(null)
  const [sandbox, setSandbox] = useState<Record<string, boolean> | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // No round trip needed for this column — we are standing in it.
  const browser: Record<string, boolean> = {}
  for (const name of PROBE_NAMES) {
    browser[name] = typeof (globalThis as Record<string, unknown>)[name] !== 'undefined'
  }

  async function probe() {
    setBusy(true)
    setError(null)
    try {
      const [srv, box] = await Promise.all([
        api<{ globals: Record<string, boolean> }>('/meta/runtime'),
        runJs(
          `const names = ${JSON.stringify(PROBE_NAMES)}\n` +
            `const out = {}\n` +
            `for (const n of names) out[n] = typeof globalThis[n] !== 'undefined'\n` +
            `console.log(JSON.stringify(out))`,
        ),
      ])
      setServer(srv.globals)
      if (box.error) throw new Error(box.error)
      setSandbox(JSON.parse(box.output[0] ?? '{}') as Record<string, boolean>)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The probe failed.')
    } finally {
      setBusy(false)
    }
  }

  const cell = (present: boolean | undefined) =>
    present === undefined ? <span className="dim">·</span> : present ? '✓' : '—'

  return (
    <div className="specimen-widget">
      <p>
        One language, three places, three different sets of globals. Press probe and every cell
        below is filled in by a live <code>typeof</code> — in your browser, in the sandbox Worker,
        and inside the Cloudflare isolate that answers this site&rsquo;s API.
      </p>
      <button
        type="button"
        className="button button-primary"
        onClick={() => void probe()}
        disabled={busy}
      >
        {busy ? 'Probing…' : 'Probe all three'}
      </button>
      {error && <div className="error-panel">{error}</div>}
      <div className="table-scroll">
        <table className="runtime-table">
          <thead>
            <tr>
              <th scope="col">global</th>
              <th scope="col">Browser</th>
              <th scope="col">Worker (sandbox)</th>
              <th scope="col">Workers (server)</th>
            </tr>
          </thead>
          <tbody>
            {PROBE_NAMES.map((name) => (
              <tr key={name}>
                <td>
                  <code>{name}</code>
                </td>
                <td>{cell(browser[name])}</td>
                <td>{cell(sandbox?.[name])}</td>
                <td>{cell(server?.[name])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="dim">
        The sandbox column is a Worker with its network globals deliberately deleted — which is
        why <code>fetch</code> is missing there and present in the other two.
      </p>
    </div>
  )
}

/** The editable sandbox. Runs the learner's code, and grants absolutely nothing. */
export function RunJsWidget() {
  const [source, setSource] = useState(
    `const game = { xp: 100, streak: 3 }\n` +
      `const copy = { ...game }\n` +
      `copy.xp = 250\n\n` +
      `console.log(game.xp, copy.xp)\n` +
      `console.log([game.xp, 'and', { nested: true }])`,
  )
  const [result, setResult] = useState<RunResult | null>(null)
  const [busy, setBusy] = useState(false)

  async function run() {
    setBusy(true)
    setResult(await runJs(source))
    setBusy(false)
  }

  return (
    <div className="specimen-widget">
      <p>
        Real JavaScript, running in a Worker inside your own browser. Nothing is uploaded and
        nothing here earns XP — this is somewhere to be wrong quickly.
      </p>
      <label className="predict-label" htmlFor="sandbox-src">
        Source
      </label>
      <textarea
        id="sandbox-src"
        className="sandbox-input"
        rows={9}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        value={source}
        onChange={(e) => setSource(e.target.value)}
      />
      <div className="specimen-controls">
        <button
          type="button"
          className="button button-primary"
          onClick={() => void run()}
          disabled={busy}
        >
          {busy ? 'Running…' : 'Run'}
        </button>
        <button
          type="button"
          className="button button-small"
          onClick={() => setResult(null)}
          disabled={busy || !result}
        >
          Clear output
        </button>
      </div>
      {result && (
        <>
          <pre className={result.error ? 'specimen-out sandbox-err' : 'specimen-out'}>
            {result.output.length > 0 ? result.output.join('\n') : '(nothing logged)'}
            {result.error ? `\n\n${result.error}` : ''}
          </pre>
          <p className="dim">
            {result.timedOut ? 'Killed by the sandbox. ' : ''}
            {result.durationMs} ms
          </p>
        </>
      )}
      <p className="dim">
        <strong>Tab moves focus</strong> instead of indenting, so the page stays navigable without
        a mouse — Lesson 7.7&rsquo;s argument, applied to the control it costs the most.
      </p>
    </div>
  )
}

/**
 * Ordering, demonstrated rather than described.
 *
 * Every snippet is chosen so that reading it top to bottom gives the WRONG
 * answer. The widget makes you commit before it will run: being surprised is
 * the mechanism, and a reader who skips to the output learns nothing they will
 * still know tomorrow.
 */
export function LoopTrace() {
  const SNIPPETS = [
    {
      label: 'sync vs microtask vs macrotask',
      source:
        `console.log('1 sync')\n` +
        `setTimeout(() => console.log('4 timeout'), 0)\n` +
        `Promise.resolve().then(() => console.log('3 microtask'))\n` +
        `console.log('2 sync')`,
    },
    {
      label: 'await is not a pause button',
      source:
        `async function go() {\n` +
        `  console.log('2 inside, before await')\n` +
        `  await null\n` +
        `  console.log('4 after await')\n` +
        `}\n` +
        `console.log('1 before the call')\n` +
        `go()\n` +
        `console.log('3 after the call')`,
    },
    {
      label: 'microtasks drain before the next timer',
      source:
        `setTimeout(() => console.log('C timeout 1'), 0)\n` +
        `setTimeout(() => console.log('D timeout 2'), 0)\n` +
        `Promise.resolve()\n` +
        `  .then(() => console.log('A microtask 1'))\n` +
        `  .then(() => console.log('B microtask 2'))`,
    },
    {
      label: 'a loop that never ends',
      source: `console.log('starting')\nwhile (true) {}\nconsole.log('never reached')`,
    },
  ]

  const [picked, setPicked] = useState(0)
  const [revealed, setRevealed] = useState<RunResult | null>(null)
  const [busy, setBusy] = useState(false)

  const snippet = SNIPPETS[picked]

  async function reveal() {
    setBusy(true)
    setRevealed(await runJs(snippet.source))
    setBusy(false)
  }

  return (
    <div className="specimen-widget">
      <p>
        Read each one and decide the output order <em>before</em> pressing reveal. The whole value
        of this widget is in being wrong about one of them.
      </p>
      <div className="specimen-controls">
        {SNIPPETS.map((s, i) => (
          <button
            key={s.label}
            type="button"
            className={i === picked ? 'button button-small button-primary' : 'button button-small'}
            onClick={() => {
              setPicked(i)
              setRevealed(null)
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      <pre className="specimen-out">{snippet.source}</pre>
      {!revealed ? (
        <button
          type="button"
          className="button button-primary"
          onClick={() => void reveal()}
          disabled={busy}
        >
          {busy ? 'Running…' : 'Reveal the real order'}
        </button>
      ) : (
        <>
          <pre className={revealed.error ? 'specimen-out sandbox-err' : 'specimen-out'}>
            {revealed.output.length > 0 ? revealed.output.join('\n') : '(nothing logged)'}
            {revealed.error ? `\n\n${revealed.error}` : ''}
          </pre>
          <button type="button" className="button button-small" onClick={() => setRevealed(null)}>
            Hide again
          </button>
        </>
      )}
    </div>
  )
}
